import { Heart, MapPin, Repeat, Users } from 'lucide-react';

import { EmptyState } from '@/components/ui/empty-state';
import { SectionHeader } from '@/components/ui/section-header';
import { Skeleton } from '@/components/ui/skeleton';
import { SoftCard } from '@/components/ui/soft-card';
import { useShop } from '@/hooks/useShop';
import { useCustomers } from '@/hooks/useCustomers';

/**
 * Customers page — aggregates from real orders via `useCustomers`.
 *
 * No hardcoded fallback data lives here. When `customers` is `null` (no
 * orders yet for the seeded shop) we render the rich empty-state preview;
 * otherwise the live KPIs + top-shoppers list.
 *
 * The "From your local area" / "Where your customers come from" panels
 * still need a postcode-distribution endpoint — for now they render a
 * neutral placeholder.
 */
export function Customers() {
	const { shop, isLoading: shopLoading } = useShop();
	const { customers, isLoading: customersLoading } = useCustomers(shop?.id);
	const loading = shopLoading || customersLoading;

	const showEmpty = !loading && !customers;

	if (loading) {
		return (
			<div className="space-y-10">
				<header>
					<h1 className="text-3xl font-extrabold tracking-tight md:text-[32px]">Customers</h1>
					<p className="mt-2 text-muted-foreground">The locals who shop with you, all in one place.</p>
				</header>
				<section className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-5">
					{[0, 1, 2].map((i) => (
						<SoftCard key={i}>
							<Skeleton className="h-3 w-1/2" />
							<Skeleton className="mt-3 h-10 w-20" />
							<Skeleton className="mt-2 h-3 w-3/4" />
						</SoftCard>
					))}
				</section>
				<section>
					<SectionHeader eyebrow="Loading" title="Regulars worth a thank you" sub="Just a sec…" />
					<SoftCard className="overflow-hidden !p-0">
						<ul className="divide-y divide-border/60">
							{[0, 1, 2, 3, 4].map((i) => (
								<li key={i} className="flex items-center gap-4 px-5 py-4 md:px-6">
									<Skeleton className="h-10 w-10 shrink-0 rounded-full" />
									<div className="min-w-0 flex-1 space-y-2">
										<Skeleton className="h-3.5 w-2/5" />
										<Skeleton className="h-3 w-1/3" />
									</div>
									<Skeleton className="h-3 w-16" />
									<Skeleton className="h-3 w-14" />
								</li>
							))}
						</ul>
					</SoftCard>
				</section>
			</div>
		);
	}

	if (showEmpty) {
		return (
			<div className="space-y-10">
				<header>
					<h1 className="text-3xl font-extrabold tracking-tight md:text-[32px]">Customers</h1>
					<p className="mt-2 text-muted-foreground">The locals who shop with you, all in one place.</p>
				</header>
				<EmptyState
					icon={Users}
					eyebrow="Build a regular crowd"
					title="Your local fanbase starts here"
					body="Every shopper who reserves or buys online appears here. Spot your regulars, see where they come from, and turn one-time visitors into loyal locals."
					primaryCta="Get your first customer"
					primaryHref="/channels"
					secondaryCta="How customers find you"
					secondaryHref="/"
					previewTitle="What you'll see here soon"
					preview={[
						{ icon: Heart, label: 'Your repeat shoppers', detail: 'The regulars worth a thank you' },
						{ icon: MapPin, label: 'Where customers come from', detail: 'Postcodes & neighbourhoods' },
						{ icon: Repeat, label: 'Lifetime value per customer', detail: 'Spot your VIPs at a glance' }
					]}
				/>
			</div>
		);
	}

	// Defensive: useCustomers returns null while orders are loading, but the
	// `loading` guard above already covers that case; this catches the post-
	// load "no orders at all" path that bypasses the empty-state branch.
	if (!customers) return null;

	return (
		<div className="space-y-10">
			<header>
				<h1 className="text-3xl font-extrabold tracking-tight md:text-[32px]">Customers</h1>
				<p className="mt-2 text-muted-foreground">The locals who shop with you, all in one place.</p>
			</header>

			<section className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-5">
				<SoftCard tone="soft">
					<div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
						<Users className="h-3.5 w-3.5" /> Total customers
					</div>
					<div className="mt-3 text-4xl font-extrabold tracking-tight">{customers.total}</div>
					<div className="mt-2 text-sm text-muted-foreground">Reserved or bought online this month</div>
				</SoftCard>
				<SoftCard>
					<div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
						<Repeat className="h-3.5 w-3.5" /> Coming back
					</div>
					<div className="mt-3 text-4xl font-extrabold tracking-tight">{customers.repeat}</div>
					<div className="mt-2 text-sm text-muted-foreground">Customers who've ordered more than once</div>
				</SoftCard>
				<SoftCard>
					<div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
						<MapPin className="h-3.5 w-3.5" /> From your local area
					</div>
					<div className="mt-3 text-4xl font-extrabold tracking-tight text-muted-foreground">—</div>
					<div className="mt-2 text-sm text-muted-foreground">Postcode distribution coming soon</div>
				</SoftCard>
			</section>

			<section>
				<SectionHeader
					eyebrow="Your top shoppers"
					title="Regulars worth a thank you"
					sub="The people who keep coming back. A friendly note next time they collect goes a long way."
				/>
				<SoftCard className="overflow-hidden !p-0">
					<ul className="divide-y divide-border/60">
						{customers.top.map((c) => (
							<li key={c.name} className="flex items-center gap-4 px-5 py-4 md:px-6">
								<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-soft text-sm font-bold text-foreground">
									{c.name
										.split(' ')
										.map((p) => p[0])
										.slice(0, 2)
										.join('')
										.toUpperCase()}
								</div>
								<div className="min-w-0 flex-1">
									<div className="truncate text-sm font-semibold text-foreground">{c.name}</div>
									{c.last ? <div className="text-xs text-muted-foreground">Last order {c.last}</div> : null}
								</div>
								<div className="text-sm tabular-nums text-muted-foreground">
									{c.orders} order{c.orders > 1 ? 's' : ''}
								</div>
								<div className="w-20 text-right text-sm font-semibold tabular-nums text-foreground">
									£{c.spend.toFixed(2)}
								</div>
								{c.repeat ? <Heart className="h-4 w-4 shrink-0 fill-action text-action" /> : null}
							</li>
						))}
					</ul>
				</SoftCard>
			</section>
		</div>
	);
}
